import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Resources – Business Turnaround Insights | JBS Consulting',
  description:
    'Expert articles and insights on business turnaround, cash flow management, debt restructuring, and fiduciary services from JBS Consulting, LLC.',
  url: `${siteUrl}/resources`,
  publisher: {
    '@type': 'Organization',
    name: 'JBS Consulting, LLC',
    url: siteUrl,
  },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'The Cash Flow Forecast: A Lifeline for Distressed Businesses' },
      { '@type': 'ListItem', position: 2, name: '5 Warning Signs Your Business Needs a Turnaround Consultant' },
      { '@type': 'ListItem', position: 3, name: 'Debt Restructuring vs. Bankruptcy: What\'s Right for Your Business?' },
      { '@type': 'ListItem', position: 4, name: 'Understanding Assignments for the Benefit of Creditors (ABC)' },
      { '@type': 'ListItem', position: 5, name: 'How to Negotiate with Creditors: A Practical Guide for Business Owners' },
      { '@type': 'ListItem', position: 6, name: 'Operational Cost Reduction: Where to Cut Without Killing Your Business' },
    ],
  },
};

export default function Resources() {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Resources' },
    { id: 'cashflow', label: 'Cash Flow' },
    { id: 'turnaround', label: 'Turnaround' },
    { id: 'restructuring', label: 'Restructuring' },
    { id: 'legal', label: 'Legal & Fiduciary' }
  ];

  const articles = [
    {
      category: 'cashflow',
      title: 'The 13-Week Cash Flow Forecast: A Lifeline for Distressed Businesses',
      excerpt: 'Learn how a rolling cash flow model gives struggling businesses the visibility and control they need to survive a financial crisis and satisfy lender requirements.',
      image: 'https://public.readdy.ai/ai/img_res/1ba6394bbfe645a3be1348002294362f.jpg',
      readTime: '8 min read',
      date: 'April 10, 2025'
    },
    {
      category: 'turnaround',
      title: '5 Warning Signs Your Business Needs a Turnaround Consultant',
      excerpt: 'Recognizing the early warning signs of financial distress can mean the difference between a successful recovery and a forced closure. Here\'s what to watch for.',
      image: 'https://readdy.ai/api/search-image?query=Business%20owner%20looking%20concerned%20while%20reviewing%20declining%20financial%20reports%20and%20charts%20at%20a%20desk%2C%20serious%20atmosphere%2C%20warm%20office%20lighting%2C%20professional%20business%20photography%2C%20documents%20showing%20downward%20trends%20and%20financial%20stress%20indicators&width=600&height=400&seq=res-turnaround-1&orientation=landscape',
      readTime: '6 min read',
      date: 'March 28, 2025'
    },
    {
      category: 'restructuring',
      title: 'Debt Restructuring vs. Bankruptcy: What\'s Right for Your Business?',
      excerpt: 'Understanding the difference between out-of-court debt restructuring and formal bankruptcy proceedings — and how to choose the right path for your situation.',
      image: 'https://readdy.ai/api/search-image?query=Legal%20and%20financial%20documents%20on%20a%20conference%20table%20with%20two%20business%20professionals%20in%20discussion%2C%20professional%20consulting%20atmosphere%2C%20bright%20modern%20office%20with%20natural%20light%2C%20clean%20composition%20showing%20legal%20paperwork%20and%20financial%20analysis%20documents&width=600&height=400&seq=res-restructuring-1&orientation=landscape',
      readTime: '10 min read',
      date: 'March 15, 2025'
    },
    {
      category: 'legal',
      title: 'Understanding Assignments for the Benefit of Creditors (ABC)',
      excerpt: 'An ABC can be a faster, less expensive alternative to bankruptcy for distressed businesses. Here\'s how the process works and when it makes sense.',
      image: 'https://readdy.ai/api/search-image?query=Attorney%20and%20financial%20advisor%20reviewing%20legal%20assignment%20documents%20in%20a%20professional%20law%20office%2C%20serious%20focused%20atmosphere%2C%20warm%20directional%20lighting%2C%20clean%20contemporary%20office%20environment%20with%20bookshelves%20and%20legal%20documents&width=600&height=400&seq=res-legal-1&orientation=landscape',
      readTime: '9 min read',
      date: 'March 5, 2025'
    },
    {
      category: 'turnaround',
      title: 'How to Negotiate with Creditors: A Practical Guide for Business Owners',
      excerpt: 'Effective creditor negotiation can buy your business the time it needs to recover. Learn proven strategies for approaching lenders and suppliers during financial distress.',
      image: 'https://readdy.ai/api/search-image?query=Business%20negotiation%20meeting%20between%20a%20consultant%20and%20bank%20representatives%20across%20a%20conference%20table%2C%20professional%20atmosphere%2C%20modern%20boardroom%20with%20natural%20lighting%2C%20serious%20but%20constructive%20discussion%2C%20documents%20and%20laptops%20on%20table&width=600&height=400&seq=res-turnaround-2&orientation=landscape',
      readTime: '7 min read',
      date: 'February 20, 2025'
    },
    {
      category: 'cashflow',
      title: 'Operational Cost Reduction: Where to Cut Without Killing Your Business',
      excerpt: 'Smart cost reduction is a critical part of any turnaround. Discover which costs to cut first, which to protect, and how to implement reductions without destroying morale.',
      image: 'https://readdy.ai/api/search-image?query=Business%20consultant%20presenting%20cost%20reduction%20analysis%20on%20a%20whiteboard%20to%20a%20small%20team%20of%20executives%2C%20collaborative%20problem-solving%20atmosphere%2C%20bright%20modern%20conference%20room%2C%20professional%20business%20photography%20with%20warm%20natural%20lighting&width=600&height=400&seq=res-cashflow-2&orientation=landscape',
      readTime: '8 min read',
      date: 'February 10, 2025'
    }
  ];

  const filteredArticles = activeCategory === 'all' ? articles : articles.filter(a => a.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <title>Resources – Business Turnaround Insights | JBS Consulting</title>
      <meta name="description" content="Expert articles on business turnaround, cash flow management, debt restructuring, and fiduciary services. Practical insights from JBS Consulting, LLC to help distressed businesses recover." />
      <meta name="keywords" content="business turnaround articles, cash flow management, debt restructuring guide, fiduciary services insights, distressed business resources" />
      <link rel="canonical" href={`${siteUrl}/resources`} />
      <meta property="og:title" content="Resources – Business Turnaround Insights | JBS Consulting" />
      <meta property="og:description" content="Expert articles and insights on business turnaround, cash flow management, and restructuring from JBS Consulting, LLC." />
      <meta property="og:url" content={`${siteUrl}/resources`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(schemaJsonLd)}</script>

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">Resources</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Expert insights and practical articles to help business owners navigate financial distress and achieve successful turnarounds.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-2.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-serif font-bold text-slate-900 mb-4">Latest Articles</h2>
            <p className="text-lg text-slate-600">Expert insights on business turnaround, restructuring, and recovery</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <div key={index} className="bg-slate-50 rounded-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative w-full h-56">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-medium text-orange-500 uppercase">{categories.find(c => c.id === article.category)?.label}</span>
                    <span className="text-xs text-slate-500">{article.date}</span>
                  </div>
                  <h3 className="text-xl font-serif font-bold text-slate-900 mb-3">{article.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">{article.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{article.readTime}</span>
                    <button className="text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors cursor-pointer">Read More →</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif font-bold text-slate-900 mb-6">Stay Informed</h2>
          <p className="text-lg text-slate-600 mb-8">
            Get monthly insights on business turnaround strategies, restructuring trends, and cash flow best practices delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input type="email" placeholder="Enter your email" className="flex-grow px-6 py-4 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
            <button className="px-8 py-4 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer">Subscribe</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}